import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import Button from "@/components/common/Button";
import { ArrowRightOutlined } from "@/config/antd";
import Copyright from "@/components/layout/Footer/Copyright";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import AuthHeader from "@/components/layout/Header/Auth_Header";
import {
  fetchRecoveryQuestionsAsync,
  submitEmailRecoveryAnswersAsync,
  selectUserInfo,
  selectRecoveryQuestions,
  selectIsFetchingRecoveryQuestions,
  selectRecoveryQuestionsError,
} from "@/store/redux/slices/auth/auth_slice";

const EmailRecovery = () => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const userInfo = useSelector(selectUserInfo);
  const recoveryQuestions = useSelector(selectRecoveryQuestions);
  const isFetchingQuestions = useSelector(selectIsFetchingRecoveryQuestions);
  const fetchError = useSelector(selectRecoveryQuestionsError);

  // One answer field per question, keyed by question index
  const [answers, setAnswers] = useState({});
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // On mount, use the email saved in Redux (set by handleForgotEmail in Email_Sign_In)
  // to fetch the user's specific security questions from the backend.
  useEffect(() => {
    const identifier = userInfo?.email;
    if (identifier) {
      dispatch(fetchRecoveryQuestionsAsync(identifier));
    }
  }, [dispatch, userInfo?.email]);

  // Surface any fetch error in the error alert
  useEffect(() => {
    if (fetchError) {
      const message =
        typeof fetchError === "string"
          ? fetchError
          : fetchError?.message || "Failed to load recovery questions. Please try again.";
      setErrorMessage(message);
      setIsErrorVisible(true);
    }
  }, [fetchError]);

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) => ({ ...prev, [index]: value }));
  };

  // At least one answer must be filled before the Proceed button activates
  const isFormFilled =
    recoveryQuestions.length > 0 &&
    Object.values(answers).some((a) => a?.trim() !== "");

  const handleProceed = async () => {
    const identifier = userInfo?.email?.trim().toLowerCase();
    if (!identifier) {
      setErrorMessage("Email identifier is missing. Please go back and try again.");
      setIsErrorVisible(true);
      return;
    }

    showLoader();
    try {
      // Collect only filled answers; each _id is the recovery question id from the fetch response.
      const answersPayload = recoveryQuestions
        .map((q, idx) => ({
          _id: q._id,
          answer: answers[idx]?.trim() ?? "",
        }))
        .filter((entry) => entry.answer !== "");

      const result = await dispatch(
        submitEmailRecoveryAnswersAsync({
          identifier,
          answers: answersPayload,
        })
      );

      hideLoader();

      if (submitEmailRecoveryAnswersAsync.fulfilled.match(result)) {
        setIsSuccessVisible(true);
      } else {
        const payload = result.payload;
        setErrorMessage(
          payload?.message ||
            payload?.error ||
            "Recovery failed. Please check your answers and try again."
        );
        setIsErrorVisible(true);
      }
    } catch (err) {
      hideLoader();
      setErrorMessage(err?.message || "Something went wrong. Please try again.");
      setIsErrorVisible(true);
    }
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
  };

  const handleCloseError = () => {
    setIsErrorVisible(false);
    setErrorMessage("");
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Top section */}
      <AuthHeader
        logoName="CMSLogo"
        logoSize="large"
        title="Have an existing Veegil SSO account?"
        titlePath="/signin"
        justifyBetween={true}
        className="px-4 py-2 md:px-6"
      />

      {/* Middle section */}
      <div className="flex flex-col px-4 md:px-6 max-w-md mx-auto w-full mt-8">
        <h1 className="heading-large whitespace-nowrap text-left text-primary mb-4">
          Account Recovery
        </h1>
        <p className="text-small text-left text-darkGrey mb-6 w-full">
          Answer the security questions below accurately based on the information
          you provided when setting up your account.
        </p>

        {/* Loading skeleton while questions are being fetched */}
        {isFetchingQuestions && (
          <div className="flex flex-col gap-4 mb-6 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-grey rounded-md" />
            ))}
          </div>
        )}

        {/* Dynamically render one input per question returned by the backend */}
        {!isFetchingQuestions &&
          recoveryQuestions.map((question, index) => {
            // Backend may return a plain string or an object with a "question" key
            const label =
              typeof question === "string" ? question : question?.question ?? `Question ${index + 1}`;

            return (
              <div key={index} className="mb-4">
                <Input
                  label={label.toUpperCase()}
                  placeholder="Enter your answer"
                  type="text"
                  value={answers[index] ?? ""}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                />
              </div>
            );
          })}

        {/* Show a friendly message if questions loaded but array is empty */}
        {!isFetchingQuestions && !fetchError && recoveryQuestions.length === 0 && (
          <p className="text-small text-darkGrey text-center mb-6">
            No recovery questions found for this account.
          </p>
        )}

        <div>
          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={isFormFilled}
            disabled={!isFormFilled}
            color="darkBlue"
            className={`w-full ${
              isFormFilled ? "bg-primary text-white" : "bg-darkBlue text-primary"
            }`}
          />
        </div>

        <div className="mt-8">
          <Copyright />
        </div>
      </div>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Recovery Message Sent"
        paragraph="We have sent an SMS to your phone number. It should contain information about your account."
        buttonText="Continue"
        recoveryPath="/signin"
      />

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Recovery Failed"
        paragraph={errorMessage}
        buttonText="Try Again"
      />
    </div>
  );
};

export default EmailRecovery;
